import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid, Button, Switch, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchOrganizationsQuery, useCreateOrganizationMutation, useUpdateOrganizationMutation } from '../features/api/rest/apiOrganizationsSlice';

function OrganizationDialog({ actionType, isOpen, setIsOpen, id = 0, oldName = '', oldSlug = '', oldIsActive = false }) {
  let title = '';
  let confirmButton = '';
  let handleConfirm = () => {};
  const { t } = useTranslation();
  const [dialogName, setDialogName] = useState(oldName || '');
  const [dialogSlug, setDialogSlug] = useState(oldSlug || '');
  const [nameError, setNameError] = useState(false);
  const [nameErrorText, setNameErrorText] = useState('');
  const [slugError, setSlugError] = useState(false);
  const [slugErrorText, setSlugErrorText] = useState('');
  const [autoSlug, setAutoSlug] = useState(actionType === 'add' ? true : false);
  const [dialogIsActive, setDialogIsActive] = useState(oldIsActive || false);
  const { data: organizations = [] } = useFetchOrganizationsQuery({ refetchOnMountOrArgChange: true });
  const [addOrganization] = useCreateOrganizationMutation();
  const [editOrganization] = useUpdateOrganizationMutation();
  useEffect(() => {
    if (isOpen) {
      setDialogName(oldName || '');
      setDialogSlug(oldSlug || '');
      setNameError(false);
      setNameErrorText('');
      setSlugError(false);
      setSlugErrorText('');
      setAutoSlug(actionType === 'add' ? true : false);
      setDialogIsActive(oldIsActive || false);
    }
  }, [isOpen]);
  const handleDialogNameChange = (e) => {
    setDialogName(e.target.value);
    if (autoSlug) {
      setDialogSlug(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase());
    }
    if (nameError) {
      setNameError(false);
    }
  };
  const handleDialogSlugChange = (e) => {
    if (autoSlug) {
      setAutoSlug(false);
    }
    setDialogSlug(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase());
    if (slugError) {
      setSlugError(false);
    }
  };
  const validation = () => {
    if (dialogName === '') {
      setNameErrorText(t('organizations.form.error.name'));
      setNameError(true);
      return false;
    }
    if (dialogSlug === '') {
      setSlugErrorText(t('organizations.form.error.slug'));
      setSlugError(true);
      return false;
    }
    if (actionType === 'edit') {
      if (organizations && organizations.some((organization) => organization.slug === dialogSlug && organization.id !== id)) {
        setSlugErrorText(t('organizations.form.error.slugExists'));
        setSlugError(true);
        return false;
      }
    } else {
      if (organizations && organizations.some((organization) => organization.slug === dialogSlug)) {
        setSlugErrorText(t('organizations.form.error.slugExists'));
        setSlugError(true);
        return false;
      }
    }
    return true;
  };
  const handleAdd = async () => {
    if (validation()) {
      try {
        const response = await addOrganization({ payload: { name: dialogName, slug: dialogSlug, is_active: dialogIsActive } });
        if (response.error) {
          throw new Error(response.error);
        } else {
          setIsOpen(false);
        }
      } catch (error) {
        setNameErrorText(t('organizations.form.error.generic'));
        setNameError(true);
        setSlugErrorText(t('organizations.form.error.generic'));
        setSlugError(true);
      }
    }
  };
  const handleEdit = async () => {
    if (validation()) {
      try {
        const response = await editOrganization({ id: id, payload: { name: dialogName, slug: dialogSlug, is_active: dialogIsActive } });
        if (response.error) {
          throw new Error(response.error);
        } else {
          setIsOpen(false);
        }
      } catch (error) {
        setNameErrorText(t('organizations.form.error.generic'));
        setNameError(true);
        setSlugErrorText(t('organizations.form.error.generic'));
        setSlugError(true);
      }
    }
  };
  switch (actionType) {
    case 'add':
      title = t('organizations.newOrganization');
      confirmButton = t('organizations.form.create');
      handleConfirm = handleAdd;
      break;
    case 'edit':
      title = t('organizations.editOrganization');
      confirmButton = t('common.actions.save');
      handleConfirm = handleEdit;
      break;
    default:
      title = '';
      confirmButton = t('common.actions.confirm');
  }
  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
      <DialogTitle sx={{ textAlign: 'center' }}>{title}</DialogTitle>
      <DialogContent fullWidth>
        <TextField
          fullWidth
          required
          value={dialogName}
          id="orgname"
          sx={{ mt: 2 }}
          error={nameError}
          helperText={nameError ? nameErrorText : ''}
          label={t('organizations.form.name')}
          name="name"
          autoComplete="name"
          inputProps={{ 'org-data': 'name' }}
          onChange={handleDialogNameChange}
        />
        <TextField
          fullWidth
          required
          value={dialogSlug}
          id="orgslug"
          sx={{ mt: 2 }}
          error={slugError}
          helperText={slugError ? slugErrorText : ''}
          label={t('organizations.form.slug')}
          name="slug"
          inputProps={{ 'org-data': 'slug' }}
          onChange={handleDialogSlugChange}
        />
        <Grid container display="flex" alignItems="center" justifyContent="left" p={0} mt={2}>
          <Grid item>
            <Switch checked={dialogIsActive} onChange={(e) => setDialogIsActive(e.target.checked)} />
          </Grid>
          <Grid item>
            <Typography>{t('organizations.form.active')}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Grid container display="flex" justifyContent="flex-end" gap={1}>
          <Grid item>
            <Button type="submit" variant="outlined" color="secondary" onClick={() => setIsOpen(false)}>
              {t('organizations.form.close')}
            </Button>
          </Grid>
          <Grid item>
            <Button type="submit" variant="contained" color="primary" onClick={handleConfirm}>
              {confirmButton}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

OrganizationDialog.propTypes = {
  actionType: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  id: PropTypes.number,
  oldName: PropTypes.string,
  oldSlug: PropTypes.string,
  oldIsActive: PropTypes.bool,
};

export default OrganizationDialog;
